import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { History, ChevronDown, ChevronUp, Undo2, Loader2, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `$${parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const KIND = {
  import: { label: "Imported", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  delete: { label: "Deleted", cls: "text-red-700 bg-red-50 border-red-200" },
  undo_import: { label: "Undid import", cls: "text-amber-700 bg-amber-50 border-amber-200" },
};

const ActionBlock = ({ a, logId, i }) => {
  const k = KIND[a.kind] || KIND.import;
  return (
    <div className="border border-stone-100 rounded-lg" data-testid={`history-action-${logId}-${i}`}>
      <div className="flex items-center gap-2 px-3 py-2 text-xs">
        <span className={`px-2 py-0.5 rounded-full border font-semibold ${k.cls}`}>{k.label} {a.count}</span>
        <span className="text-stone-500">by {a.user} · {new Date(a.at).toLocaleString()}</span>
      </div>
      <div className="border-t border-stone-100 divide-y divide-stone-50">
        {a.transactions.map(t => (
          <div key={t.id} className="flex items-center gap-3 px-3 py-1.5 text-xs">
            <span className="text-stone-500 w-20">{t.date}</span>
            <span className="text-stone-800 flex-1 truncate">{t.description || "—"}</span>
            <span className="text-stone-500 w-40 truncate">{t.vendor || "—"}</span>
            <span className="text-stone-400 w-28 truncate">{t.category}</span>
            <span className="font-medium w-24 text-right">{fmt(t.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const LogEntry = ({ lg, onUndo }) => {
  const [open, setOpen] = useState(false);
  const c = lg.counts || {};
  const cur = lg.current_counts || c;
  return (
    <div className="border-t border-stone-100 first:border-t-0" data-testid={`history-entry-${lg.id}`}>
      <div className="flex items-center gap-3 px-4 py-3 text-sm">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 min-w-0 text-left" data-testid={`history-toggle-${lg.id}`}>
          {open ? <ChevronUp className="w-4 h-4 text-stone-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-stone-400 shrink-0" />}
          <FileSpreadsheet className="w-4 h-4 text-stone-400 shrink-0" />
          <span className="font-medium text-stone-900 truncate">{lg.file_name}</span>
        </button>
        <span className="text-xs text-stone-500 whitespace-nowrap">{lg.range?.start} → {lg.range?.end}</span>
        <span className="text-xs text-stone-500 whitespace-nowrap">{lg.store_name}</span>
        <span className="text-xs text-stone-500 whitespace-nowrap" data-testid={`history-counts-${lg.id}`}>
          {c.matched} matched · {c.sheet_only} missing · {c.platform_only} extra
          {(cur.sheet_only !== c.sheet_only || cur.platform_only !== c.platform_only) && <span className="text-emerald-700"> → now {cur.sheet_only} / {cur.platform_only}</span>}
        </span>
        <span className="ml-auto text-xs text-stone-400 whitespace-nowrap">{lg.user} · {new Date(lg.created_at).toLocaleString()}</span>
        {lg.undoable_count > 0 && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onUndo(lg)} data-testid={`history-undo-btn-${lg.id}`}>
            <Undo2 className="w-3.5 h-3.5 mr-1" /> Undo import ({lg.undoable_count})
          </Button>
        )}
      </div>
      {open && (
        <div className="px-4 pb-4 space-y-2" data-testid={`history-details-${lg.id}`}>
          {lg.actions?.length ? lg.actions.map((a, i) => <ActionBlock key={i} a={a} logId={lg.id} i={i} />)
            : <p className="text-xs text-stone-400 py-2">No imports or deletions were made from this reconciliation.</p>}
        </div>
      )}
    </div>
  );
};

export const ReconcileHistory = ({ refreshKey, onDataChanged }) => {
  const [logs, setLogs] = useState(null);
  const [undoTarget, setUndoTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/reconcile/history`).then(r => setLogs(r.data)).catch(() => setLogs([]));
  useEffect(() => { load(); }, [refreshKey]);

  const undo = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/reconcile/history/${undoTarget.id}/undo-import`);
      toast.success(`Removed ${res.data.deleted_count} imported transaction${res.data.deleted_count !== 1 ? "s" : ""}`);
      onDataChanged(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Undo failed"); }
    finally { setBusy(false); setUndoTarget(null); }
  };

  return (
    <Card className="border-stone-200/60" data-testid="reconcile-history">
      <CardContent className="p-0">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-100">
          <History className="w-4 h-4 text-stone-500" />
          <h3 className="text-sm font-semibold text-stone-900">Reconcile History</h3>
          {logs && <span className="text-xs text-stone-500 bg-stone-100 rounded-full px-2 py-0.5">{logs.length}</span>}
        </div>
        {logs === null ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-stone-400 mx-auto" /></div>
          : logs.length === 0 ? <p className="text-sm text-stone-400 text-center py-8" data-testid="reconcile-history-empty">No reconciliations yet</p>
          : logs.map(lg => <LogEntry key={lg.id} lg={lg} onUndo={setUndoTarget} />)}
      </CardContent>
      <AlertDialog open={!!undoTarget} onOpenChange={(o) => !o && setUndoTarget(null)}>
        <AlertDialogContent data-testid="history-undo-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Undo import from {undoTarget?.file_name}?</AlertDialogTitle>
            <AlertDialogDescription>{undoTarget?.undoable_count} transaction(s) imported from this reconciliation will be permanently deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="history-undo-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" disabled={busy} onClick={undo} data-testid="history-undo-confirm">Undo import</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
